import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, VStack, Text, Grid, GridItem, useToast, Heading } from '@chakra-ui/react';

import SummaryStats from './components/SummaryStats';
import TopInfoSectionCard from './components/TopInfoSectionCard';
import VerifiedSellerContent from './components/VerifiedSellerContent';
import SkuScopeSection from './components/SkuScopeSection';
import MappingUploadSection from './components/MappingUploadSection';
import ItemSpecsSettingsSection from './components/ItemSpecsSettingsSection';
import ProcessControlPanel from './components/ProcessControlPanel';
import ProcessProgressSection from './components/ProcessProgressSection';
import SkuLookupSection from './components/SkuLookupSection';
import SettingsSectionCard from './components/SettingsSectionCard';

import { PROCESS_STEPS } from './constants/mockData';
import { getEntityId, getEntityName } from './utils/entityOptions.util';
import { resolveItemSpecsConfig } from './utils/itemSpecsConfig.util';

import { getMarketplacesListApi } from 'features/marketplaces/marketplaces.api';
import { getStoresListApi } from 'features/stores/stores.api';
import { getSuppliersListApi } from 'features/suppliers/suppliers.api';
import {
  getOptimizerSettingsApi,
  updateOptimizerSettingsApi,
  getOptimizerOverviewApi,
  getOptimizerProcessStatusApi,
  getOptimizerMappingInfoApi,
  getOptimizerScopeCountApi,
  uploadOptimizerMappingApi,
  startOptimizerRunApi,
  stopOptimizerRunApi,
  downloadOptimizerRunReportCsvApi,
  lookupOptimizerSkuApi,
} from 'features/listing-optimizer/listing-optimizer.api';
import { setProcessStatusFromSocket } from 'features/listing-optimizer/listing-optimizer.slice';
import socketOptimizer from '../../app/socketOptimizer';

const mapRunStepsToUi = (runState) => {
  const backendSteps = runState?.steps ?? [];
  if (backendSteps.length > 0) {
    return backendSteps.map((step) => {
      const status = step.status ?? 'idle';
      const percentage =
        status === 'completed'
          ? 100
          : status === 'running'
            ? 50
            : status === 'error'
              ? 100
              : 0;
      const counts = step.counts ?? { total: 0, succeeded: 0, failed: 0 };
      const stepProgress =
        counts.total > 0
          ? Math.round((counts.succeeded / counts.total) * 100)
          : percentage;
      return {
        id: step.id,
        label: step.label,
        description: step.description,
        status,
        percentage: stepProgress,
        message: step.message,
        counts,
      };
    });
  }
  return PROCESS_STEPS.map((template) => ({
    ...template,
    status: 'idle',
    percentage: 0,
  }));
};

const ListingOptimizer = () => {
  const dispatch = useDispatch();
  const toast = useToast();

  const { list: storeOptions } = useSelector((state) => state.stores);
  const { list: supplierOptions } = useSelector((state) => state.suppliers);
  const {
    settings,
    overview,
    mappingInfo,
    scopeCount,
    scopeCountDetail,
    getScopeCountStatus,
    processStatus,
    isOptimizerRunning,
    downloadRunReportStatus,
    lookupResult,
    lookupStatus,
    updateSettingsStatus,
  } = useSelector((state) => state.listingOptimizer);

  const [activeTopPanel, setActiveTopPanel] = useState(null);
  const [activeSettingsPanel, setActiveSettingsPanel] = useState(null);

  const [scopeMode, setScopeMode] = useState('filter');
  const [maxSkuLimit, setMaxSkuLimit] = useState(500);
  const [includeOptimizedRerun, setIncludeOptimizedRerun] = useState(false);
  const [includeHaltedRerun, setIncludeHaltedRerun] = useState(false);
  const [directListingUpdate, setDirectListingUpdate] = useState(true);
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);
  const [manualSkus, setManualSkus] = useState('');
  const [mappingFile, setMappingFile] = useState(null);

  const [skuSearchQuery, setSkuSearchQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const mappingReady = Boolean(mappingInfo?.exists || mappingFile);

  const itemSpecsConfig = useMemo(
    () => resolveItemSpecsConfig(settings?.itemSpecsConfig),
    [settings?.itemSpecsConfig],
  );

  const steps = useMemo(() => mapRunStepsToUi(processStatus), [processStatus]);
  const overallProgress = processStatus?.overallProgress ?? 0;
  const hasActiveRunningStep = steps.some((s) => s.status === 'running');
  const displayIsRunning =
    isOptimizerRunning || Boolean(processStatus?.isRunning) || hasActiveRunningStep;
  const progressMessage = processStatus?.message ?? '';
  const runSummary = processStatus?.runSummary ?? null;
  const hasRunReport = Boolean(processStatus?.hasReport);

  const overviewStats = useMemo(
    () => ({
      totalSkus: overview?.totalSkus ?? 0,
      optimizedSkus: overview?.optimizedSkus ?? 0,
      pendingSkus: overview?.pendingSkus ?? 0,
      haltedSkus: overview?.haltedSkus ?? 0,
      linkedListings: overview?.linkedListings ?? 0,
      verifiedItemSpecificsSkus: overview?.verifiedItemSpecificsSkus ?? 0,
      verifiedFitmentSkus: overview?.verifiedFitmentSkus ?? 0,
    }),
    [overview],
  );

  const manualSkuList = useMemo(
    () =>
      manualSkus
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean),
    [manualSkus],
  );

  useEffect(() => {
    const fromSettings = settings?.maxSkusPerRun;
    if (fromSettings != null && Number.isFinite(Number(fromSettings))) {
      setMaxSkuLimit(Number(fromSettings));
    }
  }, [settings?.maxSkusPerRun]);

  const estimatedSkuCount = useMemo(() => {
    if (getScopeCountStatus === 'succeeded' && scopeCount != null) {
      return scopeCount;
    }
    return null;
  }, [scopeCount, getScopeCountStatus]);

  const savedMappingMeta = useMemo(() => {
    if (!mappingInfo?.exists) return null;
    return {
      exists: true,
      fileName: mappingInfo.fileName,
      uploadedAt: mappingInfo.uploadedAt,
      fileSizeBytes: mappingInfo.fileSizeBytes,
      storedEntryCount: mappingInfo.storedEntryCount ?? mappingInfo.entryCount,
      entryCount: mappingInfo.entryCount,
    };
  }, [mappingInfo]);

  const readinessItems = useMemo(() => {
    const items = [
      {
        id: 'mapping',
        label: mappingReady
          ? `Supplier SKU → Partslink mapping uploaded${
              mappingInfo?.storedEntryCount
                ? ` (${mappingInfo.storedEntryCount.toLocaleString()} rows)`
                : ''
            }`
          : 'Upload a supplier SKU → Partslink mapping CSV',
        met: mappingReady,
      },
    ];

    if (scopeMode === 'filter') {
      const storeNames = selectedStoreIds
        .map((id) => {
          const store = storeOptions?.find((s) => getEntityId(s) === id);
          return store ? getEntityName(store) : null;
        })
        .filter(Boolean);
      items.push({
        id: 'store',
        label:
          selectedStoreIds.length > 0
            ? `Store selected for eBay API${storeNames.length ? `: ${storeNames.join(', ')}` : ''}`
            : 'Select at least one store (used for eBay API access)',
        met: selectedStoreIds.length > 0,
      });

      if (selectedSupplierIds.length > 0) {
        const supplierNames = selectedSupplierIds
          .map((id) => {
            const supplier = supplierOptions?.find((s) => getEntityId(s) === id);
            return supplier ? getEntityName(supplier) : null;
          })
          .filter(Boolean);
        items.push({
          id: 'supplier',
          label: `Supplier filter: ${supplierNames.join(', ') || selectedSupplierIds.length + ' selected'}`,
          met: true,
        });
      }
    }

    if (scopeMode === 'manual') {
      const storeNames = selectedStoreIds
        .map((id) => {
          const store = storeOptions?.find((s) => getEntityId(s) === id);
          return store ? getEntityName(store) : null;
        })
        .filter(Boolean);
      items.push({
        id: 'ebay-store',
        label:
          selectedStoreIds.length > 0
            ? `eBay API store: ${storeNames.join(', ') || 'selected'}`
            : 'Select an eBay store for API access',
        met: selectedStoreIds.length > 0,
      });
      items.push({
        id: 'manual',
        label:
          manualSkuList.length > 0
            ? `${manualSkuList.length} manual SKU${manualSkuList.length === 1 ? '' : 's'} in scope`
            : 'Add at least one SKU to the manual list',
        met: manualSkuList.length > 0,
      });
    }

    if (scopeMode === 'all') {
      items.push({
        id: 'ebay-store',
        label:
          selectedStoreIds.length > 0
            ? 'eBay API store selected'
            : 'Select an eBay store for API access',
        met: selectedStoreIds.length > 0,
      });
    }

    return items;
  }, [
    mappingReady,
    mappingInfo,
    scopeMode,
    selectedStoreIds,
    selectedSupplierIds,
    storeOptions,
    supplierOptions,
    manualSkuList.length,
  ]);

  const canStart = readinessItems.every((item) => item.met) && !displayIsRunning;

  const progressSummaryMeta = useMemo(() => {
    if (displayIsRunning || hasActiveRunningStep) {
      return { label: `${overallProgress}% running`, colorScheme: 'blue' };
    }
    if (
      overallProgress >= 100 &&
      !progressMessage?.toLowerCase().includes('interrupted')
    ) {
      return { label: 'Last run completed', colorScheme: 'green' };
    }
    if (overallProgress > 0 || processStatus?.finishedAt) {
      const interrupted = progressMessage?.toLowerCase().includes('interrupted');
      return {
        label: interrupted
          ? `${overallProgress}% interrupted`
          : `${overallProgress}% last run`,
        colorScheme: interrupted ? 'orange' : 'teal',
      };
    }
    return { label: null, colorScheme: 'teal' };
  }, [
    displayIsRunning,
    hasActiveRunningStep,
    overallProgress,
    progressMessage,
    processStatus?.finishedAt,
  ]);

  useEffect(() => {
    dispatch(getStoresListApi());
    dispatch(getSuppliersListApi());
    dispatch(getMarketplacesListApi());
    dispatch(getOptimizerSettingsApi());
    dispatch(getOptimizerOverviewApi());
    dispatch(getOptimizerMappingInfoApi());
    dispatch(getOptimizerProcessStatusApi());
  }, [dispatch]);

  const scopeCountParams = useMemo(
    () => ({
      scopeMode,
      storeIds: selectedStoreIds,
      supplierIds: selectedSupplierIds,
      manualSkus: manualSkuList,
      limit: maxSkuLimit,
      includeOptimizedRerun,
      includeHaltedRerun,
    }),
    [
      scopeMode,
      selectedStoreIds,
      selectedSupplierIds,
      manualSkuList,
      maxSkuLimit,
      includeOptimizedRerun,
      includeHaltedRerun,
    ],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (scopeMode === 'manual' && manualSkuList.length === 0) {
        return;
      }
      dispatch(getOptimizerScopeCountApi(scopeCountParams));
    }, 350);
    return () => clearTimeout(timer);
  }, [dispatch, scopeCountParams, scopeMode, manualSkuList.length]);

  useEffect(() => {
    socketOptimizer.connect();
    socketOptimizer.on('optimizer-run-state', (payload) => {
      dispatch(setProcessStatusFromSocket(payload));
    });
    socketOptimizer.on('optimizer-status', () => {
      dispatch(getOptimizerOverviewApi());
      dispatch(getOptimizerProcessStatusApi());
    });
    return () => {
      socketOptimizer.off('optimizer-run-state');
      socketOptimizer.off('optimizer-status');
      socketOptimizer.disconnect();
    };
  }, [dispatch]);

  useEffect(() => {
    if (!displayIsRunning) {
      return undefined;
    }
    const interval = setInterval(() => {
      dispatch(getOptimizerProcessStatusApi());
    }, 2000);
    return () => clearInterval(interval);
  }, [dispatch, displayIsRunning]);

  useEffect(() => {
    if (displayIsRunning || !hasSearched) return;
    const key = skuSearchQuery.trim();
    if (!key) return;
    dispatch(lookupOptimizerSkuApi(key));
  }, [displayIsRunning, hasSearched, skuSearchQuery, dispatch]);

  const handleDownloadRunReport = () => {
    dispatch(downloadOptimizerRunReportCsvApi());
  };

  const handleSaveItemSpecs = async (config) => {
    const result = await dispatch(
      updateOptimizerSettingsApi({ itemSpecsConfig: config }),
    );
    if (updateOptimizerSettingsApi.fulfilled.match(result)) {
      toast({
        title: 'Item specs settings saved',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      dispatch(getOptimizerSettingsApi());
      return true;
    }
    toast({
      title: 'Save failed',
      description: result.payload?.message || 'Could not update settings',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    return false;
  };

  const handleMappingUpload = async (file) => {
    if (!file) return;
    setMappingFile({ name: file.name, size: file.size });
    const result = await dispatch(uploadOptimizerMappingApi(file));
    if (uploadOptimizerMappingApi.fulfilled.match(result)) {
      toast({
        title: 'Mapping uploaded',
        description: `${result.payload?.entryCount ?? 0} rows saved`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      dispatch(getOptimizerMappingInfoApi());
    } else {
      toast({
        title: 'Upload failed',
        description: result.payload?.message || 'Could not save mapping file',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleStart = async () => {
    setActiveSettingsPanel('progress');
    const storeId = selectedStoreIds[0];

    const manualList = manualSkus
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);

    const result = await dispatch(
      startOptimizerRunApi({
        storeId,
        scopeMode,
        storeIds: selectedStoreIds,
        supplierIds: selectedSupplierIds,
        manualSkus: manualList,
        maxSkus: maxSkuLimit,
        includeOptimizedRerun,
        includeHaltedRerun,
        directListingUpdate,
      }),
    );

    if (startOptimizerRunApi.fulfilled.match(result)) {
      if (result.payload?.processStatus) {
        dispatch(setProcessStatusFromSocket(result.payload.processStatus));
      }
      toast({
        title: 'Optimizer started',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      dispatch(getOptimizerProcessStatusApi());
    } else {
      toast({
        title: 'Could not start',
        description: result.payload?.message || 'Start failed',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleStop = async () => {
    await dispatch(stopOptimizerRunApi());
    toast({ title: 'Stop requested', status: 'info', duration: 3000, isClosable: true });
  };

  const handleSkuSearch = async () => {
    const key = skuSearchQuery.trim();
    setHasSearched(true);
    if (!key) {
      return;
    }
    await dispatch(lookupOptimizerSkuApi(key));
  };

  const renderSettingsPanelContent = () => {
    if (activeSettingsPanel === 'progress') {
      return (
        <ProcessProgressSection
          embedded
          steps={steps}
          overallProgress={overallProgress}
          isRunning={displayIsRunning}
          currentMessage={progressMessage}
          runSummary={runSummary}
          hasReport={hasRunReport}
          onDownloadReport={handleDownloadRunReport}
          isDownloadingReport={downloadRunReportStatus === 'loading'}
        />
      );
    }

    if (activeSettingsPanel === 'mapping') {
      return (
        <MappingUploadSection
          embedded
          mappingFile={mappingFile}
          savedMapping={savedMappingMeta}
          onMappingFileChange={setMappingFile}
          onFileSelected={handleMappingUpload}
          previewRows={
            mappingInfo?.sample?.map((r) => ({
              sku: r.supplierSku,
              partslink: r.partslink,
            })) ?? []
          }
        />
      );
    }

    if (activeSettingsPanel === 'specs') {
      return (
        <ItemSpecsSettingsSection
          embedded
          itemSpecsConfig={itemSpecsConfig}
          onSave={handleSaveItemSpecs}
          isSaving={updateSettingsStatus === 'loading'}
          storeOptions={storeOptions ?? []}
          supplierOptions={supplierOptions ?? []}
        />
      );
    }

    return null;
  };

  return (
    <Box p={6} mt="50px">
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" color="gray.700" mb={1}>
            Listing Optimizer
          </Heading>
          <Text color="gray.500" fontSize="sm">
            Fetch verified eBay data, update item specifics, and sync linked listings per
            canonical SKU. One SKU can power multiple marketplace listings.
          </Text>
        </Box>

        <SummaryStats overview={overviewStats} />

        <TopInfoSectionCard
          activePanel={activeTopPanel}
          onPanelChange={setActiveTopPanel}
        >
          {activeTopPanel === 'seller' && <VerifiedSellerContent />}
          {activeTopPanel === 'lookup' && (
            <Box
              p={5}
              bg="white"
              borderRadius="lg"
              borderWidth="1px"
              borderColor="gray.200"
              boxShadow="sm"
            >
              <SkuLookupSection
                embedded
                searchQuery={skuSearchQuery}
                onSearchQueryChange={setSkuSearchQuery}
                onSearch={handleSkuSearch}
                lookupResult={lookupStatus === 'succeeded' ? lookupResult : null}
                hasSearched={hasSearched}
                isSearching={lookupStatus === 'loading'}
              />
            </Box>
          )}
        </TopInfoSectionCard>

        <SettingsSectionCard
          activePanel={activeSettingsPanel}
          onPanelChange={setActiveSettingsPanel}
          progressSummary={progressSummaryMeta.label}
          progressBadgeColorScheme={progressSummaryMeta.colorScheme}
          mappingReady={mappingReady}
        >
          {renderSettingsPanelContent()}
        </SettingsSectionCard>

        <Grid
          templateColumns={{ base: '1fr', xl: '3fr 1fr' }}
          gap={4}
          alignItems="stretch"
        >
          <GridItem>
            <SkuScopeSection
              scopeMode={scopeMode}
              onScopeModeChange={setScopeMode}
              selectedStoreIds={selectedStoreIds}
              onStoreChange={setSelectedStoreIds}
              selectedSupplierIds={selectedSupplierIds}
              onSupplierChange={setSelectedSupplierIds}
              manualSkus={manualSkus}
              onManualSkusChange={setManualSkus}
              maxSkuLimit={maxSkuLimit}
              onMaxSkuLimitChange={setMaxSkuLimit}
              includeOptimizedRerun={includeOptimizedRerun}
              onIncludeOptimizedRerunChange={setIncludeOptimizedRerun}
              includeHaltedRerun={includeHaltedRerun}
              onIncludeHaltedRerunChange={setIncludeHaltedRerun}
              storeOptions={storeOptions ?? []}
              supplierOptions={supplierOptions ?? []}
            />
          </GridItem>
          <GridItem>
            <ProcessControlPanel
              isRunning={displayIsRunning}
              canStart={canStart}
              readinessItems={readinessItems}
              onStart={handleStart}
              onStop={handleStop}
              estimatedSkuCount={estimatedSkuCount}
              scopeCountDetail={scopeCountDetail}
              scopeCountLoading={getScopeCountStatus === 'loading'}
              directListingUpdate={directListingUpdate}
              onDirectListingUpdateChange={setDirectListingUpdate}
            />
          </GridItem>
        </Grid>
      </VStack>
    </Box>
  );
};

export default ListingOptimizer;
